import FlexSearch, { Index } from "flexsearch";
import { parseFrontmatter } from "./frontmatterParser";
import { ContentPage } from "./contentLoader";

export interface SearchResult {
  page: ContentPage;
  title: string;
  description?: string;
  excerpt?: string;
  score: number;
  matches?: {
    field: string;
    text: string;
  }[];
}

export interface SearchIndex {
  index: Index;
  documents: Map<string, SearchDocument>;
}

export interface SearchDocument {
  id: string;
  page: ContentPage;
  title: string;
  description?: string;
  content: string;
  headings: string[];
  tags?: string[];
}

/**
 * Create a new search index
 */
export function createSearchIndex(): SearchIndex {
  const index = new FlexSearch.Index({
    tokenize: "forward",
    cache: 100,
    preset: "performance",
  });

  return {
    index,
    documents: new Map(),
  };
}

/**
 * Add a document to the search index
 */
export function addToSearchIndex(
  searchIndex: SearchIndex,
  page: ContentPage,
  markdownContent: string,
): void {
  const { frontmatter, content } = parseFrontmatter(markdownContent);

  // Extract headings from content
  const headingRegex = /^#{2,3}\s+(.+)$/gm;
  const headings: string[] = [];
  let match;
  while ((match = headingRegex.exec(content)) !== null) {
    headings.push(match[1].trim());
  }

  // Create searchable text
  const searchableText = [
    frontmatter.title,
    frontmatter.description || "",
    ...headings,
    ...(frontmatter.tags || []),
    content.replace(/^#+\s+/gm, ""), // Remove markdown headings
  ]
    .filter(Boolean)
    .join(" ");

  const doc: SearchDocument = {
    id: page.path,
    page,
    title: frontmatter.title,
    description: frontmatter.description,
    content: searchableText,
    headings,
    tags: frontmatter.tags,
  };

  searchIndex.documents.set(page.path, doc);
  searchIndex.index.add(page.path, searchableText);
}

/**
 * Search the index
 */
export function search(
  searchIndex: SearchIndex,
  query: string,
  limit = 10,
): SearchResult[] {
  if (!query.trim()) {
    return [];
  }

  const results = searchIndex.index.search(query, {
    limit,
    suggest: true,
  }) as string[];

  const searchResults: SearchResult[] = [];

  results.forEach((id) => {
    const doc = searchIndex.documents.get(id);
    if (!doc) return;

    // Calculate a simple score based on position
    const score = 1 / (results.indexOf(id) + 1);

    searchResults.push({
      page: doc.page,
      title: doc.title,
      description: doc.description,
      excerpt: doc.description || doc.content.substring(0, 150) + "...",
      score,
    });
  });

  return searchResults;
}
